
import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import StickyCTA from './components/StickyCTA';
import SplashScreen from './components/SplashScreen';
import Seo from './components/Seo';
import FloatingChatbot from './components/FloatingChatbot';
import { AppProvider } from './context/AppContext';
import { NotificationProvider } from './context/NotificationContext';

// Eager Load Critical Pages
import Home from './pages/Home';
import Owners from './pages/Owners';

// Lazy Load Secondary Pages
const WishlistPage = lazy(() => import('./pages/WishlistPage'));
const ShopOwnerDemoPage = lazy(() => import('./pages/ShopOwnerDemoPage'));

const PartnerForm = lazy(() => import('./pages/PartnerForm'));
const Documentation = lazy(() => import('./pages/Documentation'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const LegalPage = lazy(() => import('./pages/LegalPage'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Careers = lazy(() => import('./pages/Careers'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminInbox = lazy(() => import('./pages/AdminInbox'));
const SurveyPage = lazy(() => import('./pages/SurveyPage'));

const UnsubscribePage = lazy(() => import('./pages/UnsubscribePage'));
const SubscribePage = lazy(() => import('./pages/SubscribePage'));
const PlansPage = lazy(() => import('./pages/PlansPage'));
const DoctorsPage = lazy(() => import('./pages/DoctorsPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PartnerPortalPage = lazy(() => import('./pages/PartnerPortalPage'));
const DoctorPortalDemoPage = lazy(() => import('./pages/DoctorPortalDemoPage'));
const HospitalPortalDemoPage = lazy(() => import('./pages/HospitalPortalDemoPage'));
const LabPortalDemoPage = lazy(() => import('./pages/LabPortalDemoPage'));


const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Wrapper for pages to inject SEO
const PageWrapper = ({ children, title, desc, keywords, jsonLd }: React.PropsWithChildren<{ title: string, desc: string, keywords?: string, jsonLd?: Record<string, any> }>) => (
    <>
        <Seo title={title} description={desc} keywords={keywords} jsonLd={jsonLd} />
        {children}
    </>
);

const AppContent = () => {
  const location = useLocation();

  // Hide Navbar/Footer/CTA on Admin routes, Shop Demo route, AND Partner Portal routes
  const isFullScreenRoute = location.pathname.startsWith('/admin') || 
                            location.pathname === '/shop-demo' || 
                            location.pathname === '/partner-portal' ||
                            location.pathname === '/doctor-demo' ||
                            location.pathname === '/hospital-demo' ||
                            location.pathname === '/lab-demo';

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 selection:bg-blue-100 selection:text-blue-900 flex flex-col font-sans">
      <ScrollToTop />
      {!isFullScreenRoute && <Navbar />}
      
      <main className="flex-grow">
        <Suspense fallback={
          <div className="min-h-screen flex items-center justify-center">
             <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          </div>
        }>
          <Routes>
            <Route path="/" element={<PageWrapper
              title="Pharmelo — India's Smart Pharmacy App & Doctor Booking Platform"
              desc="Order medicines from verified local chemist shops for 15-minute pickup or fast delivery. Upload prescriptions via WhatsApp, consult top local doctors online or in-person, and manage digital health records on Pharmelo. Live in Solan, Shimla, Himachal Pradesh."
              keywords="Pharmelo, smart pharmacy app India, order medicine online Solan, doctor booking Shimla, local chemist store Solan, WhatsApp prescription upload, 15 minute medicine pickup, medical store near me, local pharmacy Himachal"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Pharmelo — Smart Pharmacy & Doctor Booking",
                "description": "Order medicines from local chemists for 15-min pickup. Upload prescriptions via WhatsApp, book doctors, manage health records.",
                "url": "https://pharmelo.in/",
                "mainEntity": { "@id": "https://pharmelo.in/#app" }
              }}
            ><Home /></PageWrapper>} />
            <Route path="/owners" element={<PageWrapper
              title="Pharmelo for Pharmacy Owners | Digital Pharmacy SaaS & Sales Growth"
              desc="Partner with Pharmelo to digitize your chemist shop, process digital prescriptions, manage inventory, and increase medicine orders. The ultimate local pharmacy POS and SaaS platform in India."
              keywords="partner with Pharmelo, pharmacy software India, medical store online sales, chemist billing POS, increase pharmacy revenue, digital chemist shop"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Pharmelo for Pharmacy Owners",
                "description": "Partner with Pharmelo to increase sales and streamline operations for your pharmacy.",
                "url": "https://pharmelo.in/owners"
              }}
            ><Owners /></PageWrapper>} />
            <Route path="/wishlist" element={<PageWrapper 
              title="Try Pharmelo Demo | Live Smart Pharmacy App Simulator" 
              desc="Experience the future of local pharmacy retail. Try the interactive Pharmelo app simulator to test WhatsApp prescription reading, 15-minute medicine pre-order, and doctor booking."
              keywords="Pharmelo app demo, pharmacy simulator, test medicine order online, online doctor slot booking demo"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Pharmelo App Demo Simulator",
                "description": "Interactive demo of the Pharmelo patient app.",
                "url": "https://pharmelo.in/wishlist"
              }}
            ><WishlistPage /></PageWrapper>} />
            <Route path="/shop-demo" element={<PageWrapper 
              title="Pharmelo Partner Dashboard Demo | Pharmacy Management Portal" 
              desc="Simulate managing orders, tracking prescriptions, verifying medical inventory, and accepting digital orders as a chemist store owner on Pharmelo's partner platform."
              keywords="pharmacy dashboard demo, medical shop inventory portal, chemist order manager software"
            ><ShopOwnerDemoPage /></PageWrapper>} />
            <Route path="/roadmap" element={<PageWrapper 
              title="Pharmelo Roadmap | Launch Timeline & Retail Digitalization Timeline" 
              desc="Track our progress and timeline in building India's smart pharmacy network. See upcoming expansion cities, core feature rollouts, and launch schedules."
              keywords="Pharmelo roadmap, pharmacy startup timeline, healthtech launch India, medical store expansion Solan Shimla"
            ><RoadmapPage /></PageWrapper>} />

            <Route path="/partner-form" element={<PageWrapper 
              title="Apply as a Pharmacy Partner | Pharmelo Chemist Network" 
              desc="Register your retail medical store, chemist shop, or pharmacy as a Pharmelo partner to start receiving online pre-orders and digitizing your local business."
              keywords="pharmacy registration, register chemist shop, partner medical store, join Pharmelo"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Pharmelo Partner Registration",
                "description": "Register your pharmacy as a Pharmelo partner.",
                "url": "https://pharmelo.in/partner-form"
              }}
            ><PartnerForm /></PageWrapper>} />
            <Route path="/documentation" element={<PageWrapper 
              title="Partner Onboarding Documentation & Guides | Pharmelo Portal" 
              desc="Access technical manuals, customer order fulfillment guidelines, AI prescription verification processes, and onboarding documents for Pharmelo pharmacy partners."
              keywords="pharmacy guides, chemist onboarding documentation, Pharmelo partner manual"
            ><Documentation /></PageWrapper>} />
            <Route path="/terms" element={<PageWrapper 
              title="Terms of Service & User Agreement | Pharmelo Healthcare" 
              desc="Review the legal terms, rules, and user agreement for ordering medicines and booking doctor appointments on the Pharmelo platform."
              keywords="Pharmelo terms, user agreement, pharmacy legal terms"
            ><LegalPage type="terms" /></PageWrapper>} />
            <Route path="/privacy" element={<PageWrapper 
              title="Privacy Policy & Health Data Protection | Pharmelo" 
              desc="Learn how Pharmelo protects your medical history, prescription details, private health records, and personal information in accordance with healthcare data laws."
              keywords="privacy policy, medical data safety, health records encryption"
            ><LegalPage type="privacy" /></PageWrapper>} />
            <Route path="/about" element={<PageWrapper
              title="About Pharmelo | Sumit Kumar Smart Pharmacy Startup"
              desc="Discover the story of Pharmelo, founded by Sumit Kumar (a college going student) in collaboration with Shudhosan Skills Solution Private Limited to solve long chemist queues and prescription reading problems. Connect with local pharmacies for 15-min medicine pickup."
              keywords="about Pharmelo, Sumit Kumar, Shudhosan Skills Solution Private Limited, college going student startup, pharmacy founders India, digital health Solan Shimla"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "AboutPage",
                "name": "About Pharmelo",
                "description": "The story behind Pharmelo — India's smart pharmacy platform.",
                "url": "https://pharmelo.in/about",
                "mainEntity": { "@id": "https://pharmelo.in/#organization" }
              }}
            ><AboutUs /></PageWrapper>} />
            <Route path="/careers" element={<PageWrapper 
              title="Careers at Pharmelo | Join India's Fast-Growing HealthTech Team" 
              desc="Work with us to digitize local pharmacies and empower retail chemists. Apply for software engineering, operations, and marketing roles or internships."
              keywords="Pharmelo jobs, healthtech careers India, hire software developer Solan Shimla, pharmacy startup internships"
              jsonLd={{
                "@context": "https://schema.org",
                "@type": "WebPage",
                "name": "Pharmelo Careers",
                "description": "Join the Pharmelo team and help digitize healthcare in India.",
                "url": "https://pharmelo.in/careers"
              }}
            ><Careers /></PageWrapper>} />
 
             {/* Admin Routes */}
             <Route path="/admin" element={<PageWrapper title="Admin Login | Pharmelo" desc="Restricted access."><AdminLogin /></PageWrapper>} />
             <Route path="/admin/dashboard" element={<PageWrapper title="Admin Dashboard | Pharmelo" desc="Restricted access."><AdminDashboard /></PageWrapper>} />
             <Route path="/admin/inbox" element={<PageWrapper title="Admin Inbox | Pharmelo" desc="Restricted access."><AdminInbox /></PageWrapper>} />
             <Route path="/survey" element={<PageWrapper 
              title="Pharmelo Healthcare Survey | Share Your Feedback" 
              desc="Help us customize the smart pharmacy platform for your needs. Participate in our short health survey as a patient, doctor, or pharmacist."
              keywords="Pharmelo survey, chemist shop feedback, healthcare feedback form, doctor booking survey"
             ><SurveyPage /></PageWrapper>} />

             <Route path="/unsubscribe" element={<UnsubscribePage />} />
             <Route path="/subscribe" element={<SubscribePage />} />
             <Route path="/plans" element={<PageWrapper 
              title="Pharmelo SaaS Subscription Plans & Pricing" 
              desc="Compare Pharmelo's B2B plans for pharmacies, doctors, and hospitals. Flexible monthly or annual pricing with clinical AI tools."
              keywords="Pharmelo plans, pharmacy SaaS price, doctor dashboard subscription, healthtech aggregator pricing"
             ><PlansPage /></PageWrapper>} />
             <Route path="/doctors" element={<PageWrapper 
              title="Pharmelo for Doctors & Hospitals | Digital Clinical Tools" 
              desc="Manage patient appointments, issue e-prescriptions, coordinate multi-doctor schedules, and integrate with ABDM using Pharmelo's clinical dashboard."
              keywords="doctor booking platform India, hospital digital tools, e-prescription software, ABDM health ID integration, clinic management system"
             ><DoctorsPage /></PageWrapper>} />
             <Route path="/contact" element={<PageWrapper 
              title="Contact Pharmelo | Get in Touch" 
              desc="Reach out to the Pharmelo team for partnership enquiries, pharmacy or doctor registration, media queries, or general support."
              keywords="contact Pharmelo, pharmacy partnership enquiry, doctor registration Pharmelo, Pharmelo support"
             ><ContactPage /></PageWrapper>} />

             <Route path="/partner-portal" element={<PageWrapper 
              title="Pharmelo Unified Partner Portal Demo | Clinical & Lab Dashboard" 
              desc="Interactive simulation of the doctor e-prescription tool, hospital live OPD queue and bed roster, and diagnostic lab report delivery dashboard."
              keywords="doctor portal demo, clinic queue management, lab report delivery software, hospital dashboard simulator"
             ><PartnerPortalPage /></PageWrapper>} />

              <Route path="/doctor-demo" element={<PageWrapper 
               title="Doctor Portal Demo | Pharmelo Partner Dashboard" 
               desc="Simulate e-prescriptions, track appointments, and teleconsult patients."
              ><DoctorPortalDemoPage /></PageWrapper>} />
 
              <Route path="/hospital-demo" element={<PageWrapper 
               title="Hospital Portal Demo | Pharmelo Partner Dashboard" 
               desc="Simulate doctor duty rosters, OPD queues, and live bed occupancy."
              ><HospitalPortalDemoPage /></PageWrapper>} />
 
              <Route path="/lab-demo" element={<PageWrapper 
               title="Lab Portal Demo | Pharmelo Partner Dashboard" 
               desc="Simulate test catalogues, barcode tracking, and report dispatches."
              ><LabPortalDemoPage /></PageWrapper>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>

      {!isFullScreenRoute && <Footer />}
      {!isFullScreenRoute && <StickyCTA />}
      {!isFullScreenRoute && <FloatingChatbot />}
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <NotificationProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </NotificationProvider>
    </AppProvider>
  );
}

export default App;
